import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box, Button, Chip, InputAdornment, MenuItem, Paper, Stack, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Typography, useMediaQuery, useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import type { Partner, PartnerRelationshipType } from '../api/types';
import { PARTNER_RELATIONSHIP_TYPES } from '../api/types';
import { usePartners } from '../api/hooks';
import { PartnerFormDialog } from '../components/forms/PartnerFormDialog';
import { EmptyState, ErrorState, LoadingState, UserText } from '../components/common/Common';
import { formatPercent } from '../components/common/format';

export function PartnersPage() {
  const { t } = useTranslation();
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'));

  const [term, setTerm] = useState('');
  const [debounced, setDebounced] = useState('');
  const [typeFilter, setTypeFilter] = useState<PartnerRelationshipType | ''>('');
  const [dialog, setDialog] = useState<{ open: boolean; partner: Partner | null }>({ open: false, partner: null });

  const { data, isLoading, error, refetch } = usePartners(debounced);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(term), 300);
    return () => clearTimeout(timer);
  }, [term]);

  // The type filter is applied here rather than server-side: the list is capped at 100 rows and
  // already in memory, so a round trip per dropdown change would be pure latency.
  const rows = (data?.items ?? []).filter(
    (p) => !typeFilter || p.defaultRelationshipType === typeFilter,
  );

  const margin = (partner: Partner) =>
    partner.defaultMarginPercent === null ? '—' : `${formatPercent(partner.defaultMarginPercent)} %`;

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', sm: 'center' }}
        spacing={2}
        sx={{ mb: 2.5 }}
      >
        <Typography variant="h1">{t('partner.partners')}</Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
          <TextField
            size="small"
            placeholder={t('common.filterByName')}
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            inputProps={{ dir: 'auto', 'data-testid': 'partner-filter' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
              ),
            }}
          />
          <TextField
            select
            size="small"
            label={t('common.type')}
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value as PartnerRelationshipType | '')}
            sx={{ minWidth: 170 }}
            data-testid="partner-type-filter"
            SelectProps={{ displayEmpty: true }}
            InputLabelProps={{ shrink: true }}
          >
            <MenuItem value="">{t('partner.allTypes')}</MenuItem>
            {PARTNER_RELATIONSHIP_TYPES.map((type) => (
              <MenuItem key={type} value={type}>{t(`partner.relationship.${type}`)}</MenuItem>
            ))}
          </TextField>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setDialog({ open: true, partner: null })}
            data-testid="new-partner"
          >
            {t('partner.newPartner')}
          </Button>
        </Stack>
      </Stack>

      {isLoading && <LoadingState label={t('partner.loading')} />}
      {error && <ErrorState error={error} onRetry={() => void refetch()} />}

      {!isLoading && !error && rows.length === 0 && (
        <Paper variant="outlined" sx={{ borderRadius: '10px' }}>
          <EmptyState
            title={term || typeFilter ? t('partner.noMatch') : t('partner.empty')}
            description={term || typeFilter ? t('partner.noMatchDescription') : t('partner.emptyDescription')}
            action={
              !term && !typeFilter ? (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setDialog({ open: true, partner: null })}
                >
                  {t('partner.newPartner')}
                </Button>
              ) : undefined
            }
          />
        </Paper>
      )}

      {rows.length > 0 && (isPhone ? (
        <Stack spacing={1.5} data-testid="partners-list">
          {rows.map((partner) => (
            <Paper
              key={partner.id}
              variant="outlined"
              sx={{ p: 2, borderRadius: '10px', cursor: 'pointer' }}
              onClick={() => setDialog({ open: true, partner })}
              data-testid={`partner-row-${partner.id}`}
            >
              <Typography variant="h4"><UserText>{partner.name}</UserText></Typography>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.75 }} flexWrap="wrap" useFlexGap>
                <Chip size="small" variant="outlined" label={t(`partner.relationship.${partner.defaultRelationshipType}`)} />
                <Typography variant="caption" color="text.secondary">
                  {t('partner.margin')}: {margin(partner)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('partner.columns.customers')}: {partner.customerCount}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('partner.columns.opportunities')}: {partner.openOpportunityCount}
                </Typography>
              </Stack>
            </Paper>
          ))}
        </Stack>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: '10px', overflowX: 'auto' }}>
          <Table data-testid="partners-table">
            <TableHead>
              <TableRow>
                <TableCell>{t('partner.columns.name')}</TableCell>
                <TableCell>{t('partner.columns.type')}</TableCell>
                <TableCell align="right">{t('partner.columns.margin')}</TableCell>
                <TableCell align="right">{t('partner.columns.customers')}</TableCell>
                <TableCell align="right">{t('partner.columns.opportunities')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((partner) => (
                <TableRow
                  key={partner.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => setDialog({ open: true, partner })}
                  data-testid={`partner-row-${partner.id}`}
                >
                  <TableCell><UserText>{partner.name}</UserText></TableCell>
                  <TableCell>{t(`partner.relationship.${partner.defaultRelationshipType}`)}</TableCell>
                  <TableCell align="right">{margin(partner)}</TableCell>
                  <TableCell align="right">{partner.customerCount}</TableCell>
                  <TableCell align="right">{partner.openOpportunityCount}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ))}

      <PartnerFormDialog
        open={dialog.open}
        partner={dialog.partner}
        onClose={() => setDialog({ open: false, partner: null })}
      />
    </Box>
  );
}
